import React, { useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';

// Route name mappings for better display
const routeNames = {
  'about-amy': 'About Amy',
  'maritime-injuries': 'Maritime Injuries',
  'car-crashes': 'Car Accidents',
  'first-responder-crashes': 'First Responder Crashes',
  'motorcycle-crashes': 'Motorcycle Accidents',
  'slip-and-falls': 'Slip and Falls',
  'med-malpractice': 'Medical Malpractice',
  'wrongful-death': 'Wrongful Death',
  'is-amy-right-for-you': 'Is Amy Right For You?',
  'areas-we-serve': 'Areas We Serve',
  'faqs': 'FAQs',
  'pricing': 'Pricing',
  'compensation-estimator': 'Case Calculator',
  'contact': 'Contact',
  'blog': 'Blog',
  'service-area': 'Service Areas',
  'everett-slip-and-fall-lawyer': 'Everett Slip & Fall',
  'tacoma-slip-and-fall-lawyer': 'Tacoma Slip & Fall',
  'bellingham-slip-and-fall-lawyer': 'Bellingham Slip & Fall',
  'female-personal-injury-attorney': 'Female Personal Injury Attorney',
};

const Breadcrumbs = ({ customItems, className = '' }) => {
  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(Boolean);
  
  // Check if we should show breadcrumbs
  const shouldShow = pathSegments.length > 0 && pathSegments[0] !== 'admin';

  // Build breadcrumb items using useMemo
  const breadcrumbItems = useMemo(() => {
    if (!shouldShow) return [];
    if (customItems) return customItems;
    
    const items = [{ name: 'Home', path: '/' }];
    let currentPath = '';
    
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      
      // Handle service-area dynamic routes
      if (segment === 'service-area' && pathSegments[index + 1]) {
        items.push({ name: 'Service Areas', path: '/areas-we-serve' });
        return;
      }
      
      // Skip the slug after service-area (handled by parent)
      if (pathSegments[index - 1] === 'service-area') {
        // Format city name from slug
        const cityName = segment
          .split('-')
          .filter(word => !['personal', 'injury', 'lawyer', 'wa'].includes(word))
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        items.push({ name: cityName, path: currentPath });
        return;
      }
      
      // Handle blog post routes
      if (segment === 'blog' && pathSegments[index + 1]) {
        items.push({ name: 'Blog', path: '/blog' });
        return;
      }
      
      if (pathSegments[index - 1] === 'blog') {
        const postName = segment
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        items.push({ name: postName.length > 40 ? postName.substring(0, 40) + '...' : postName, path: currentPath });
        return;
      }
      
      const name = routeNames[segment] || segment.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      items.push({ name, path: currentPath });
    });
    
    return items;
  }, [pathSegments, customItems, shouldShow]);

  // Add breadcrumb schema to document head
  useEffect(() => {
    if (!shouldShow || breadcrumbItems.length === 0) return;
    
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbItems.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
        "item": `https://amycbrownlaw.com${item.path}`
      }))
    };
    
    // Create script element
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'breadcrumb-schema';
    script.textContent = JSON.stringify(breadcrumbSchema);
    
    // Remove old script if exists
    const oldScript = document.getElementById('breadcrumb-schema');
    if (oldScript) {
      oldScript.remove();
    }
    
    document.head.appendChild(script);
    
    return () => {
      const existingScript = document.getElementById('breadcrumb-schema');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [breadcrumbItems, shouldShow]);

  // Don't show breadcrumbs on homepage or admin routes
  if (!shouldShow) {
    return null;
  }

  return (
    <nav 
      aria-label="Breadcrumb" 
      className={`py-3 px-4 bg-slate-50 border-b border-slate-100 ${className}`}
      data-testid="breadcrumbs"
    >
      <div className="container mx-auto max-w-7xl">
        <ol className="flex flex-wrap items-center gap-1 text-sm">
          {breadcrumbItems.map((item, index) => {
            const isLast = index === breadcrumbItems.length - 1;
            
            return (
              <li key={item.path} className="flex items-center">
                {index > 0 && (
                  <ChevronRight className="w-4 h-4 text-slate-400 mx-1" />
                )}
                
                {isLast ? (
                  <span 
                    className="text-slate-600 font-medium"
                    aria-current="page"
                  >
                    {index === 0 ? <Home className="w-4 h-4" /> : item.name}
                  </span>
                ) : (
                  <Link 
                    to={item.path}
                    className="text-teal-600 hover:text-teal-700 hover:underline flex items-center gap-1"
                  >
                    {index === 0 ? <Home className="w-4 h-4" /> : item.name}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
};

export default Breadcrumbs;
